import { Divider, Grid, InputAdornment } from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import { ToggleButtonGroup } from '@material-ui/lab';
import MuiToggleButton from '@material-ui/lab/ToggleButton';
import {
  AutoComplete,
  Button,
  Dialog,
  Icons,
  TextField,
  Typography,
} from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  assignInspector,
  emailForTaskAssignment,
  saveFieldInspectorApi,
  saveInspectionComment,
} from '../../graphql';
import { bulkAssignOrReassign } from '../../graphql/InspectorOperations';
import { useMst } from '../../mobx-models/Root';
import { IViewInspectorDocument } from '../../models/models';
import { useInspectionList } from '../../rxdb/inspectionList/useInspectionList';
import { TaskList } from '../../rxdb/taskList/queryBuilder';
import useSelectedTask from '../../rxdb/taskList/useSelectedTask';
import { useRxDB } from '../../rxdb/useRxDB';
import { ViewInspectorDocument } from '../../rxdb/viewInspector/queryBuilder';
import { isJestTest } from '../../test-helpers/isJestTest';
import { sentryCaptureException, useNotification, useOnlineStatus } from '../../utilities';
import { TRUE } from '../../utilities/constants';
import {
  BatchSize,
  FieldInspectorType,
  InspectorRole,
  Key,
  TaskStatus,
} from '../../utilities/enums';
import { sortByInspectorName } from '../../utilities/sortHelper';
import { getValueFromLocalStorage, InspectorData, isNull } from '../../utilities/utilityFunctions';
import CustomButton from '../CustomButton/CustomButton';
import DeleteTask from '../DeleteTask';
import StyledButton from '../InspectorInfo/StyledButton';
import AddInspector from './AddInspectors';
import useStyles from './AssignInspector.styles';

const ToggleButton = styled(MuiToggleButton)(({}) => ({
  '&.Mui-selected:not(.Mui-disabled), &.Mui-selected:hover:not(.Mui-disabled)': {
    color: '#121212',
    backgroundColor: 'rgba(42, 171, 63, 1)',
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    color: '#121212',
    backgroundColor: 'dimgray',
  },
}));

interface ISelectedInspector {
  fieldInspectorId: number;
  fieldInspectorName: string;
  isActive: boolean;
  taskUuid?: string;
  taskFiId?: number;
  taskStatus?: number;
  recommendations?: string;
  dateCompleted?: string;
  clerkId?: number;
  clerkName?: string;
}

interface ISelectedUniqueInspector {
  fieldInspectorId: number;
  fieldInspectorName: string;
  isActive: boolean;
  taskUuid?: string;
  count?: number;
  taskFiId?: number;
  taskStatus?: number;
  recommendations?: string;
  dateCompleted?: string;
  clerkId?: number;
  clerkName?: string;
}

interface Props {
  openAssignInspector: boolean;
  setOpenAssignInspector: (val: boolean) => void;
  fieldInspectorList?: ViewInspectorDocument[];
  leadInspectorId: number;
  inspectionId: number;
  taskList?: TaskList[];
  inspectionLevelComment: string;
  title: string;
  isUpdateInspector: boolean;
  isJestTestCase?: boolean;
  isAssignToLead: boolean;
  selectedProxyInspector: { fieldInspectorName: string };
  handleProxyInspectorChange: (val: { fieldInspectorName: '' }) => void;
  taskId: number;
  isFromInspectedBy: boolean;
}

const AssignInspector: React.FC<Props> = ({
  openAssignInspector,
  setOpenAssignInspector,
  fieldInspectorList,
  leadInspectorId,
  inspectionId,
  taskList,
  title,
  isUpdateInspector,
  inspectionLevelComment,
  isJestTestCase = 'false',
  isAssignToLead,
  selectedProxyInspector,
  taskId,
  isFromInspectedBy,
  handleProxyInspectorChange,
}) => {
  const classes = useStyles();
  const { HandleGesture, CurrentUserSelected, SiteDiscipline } = useMst();
  const { db } = useRxDB();
  const loggedInUser = CurrentUserSelected?.currentUser;
  const online = useOnlineStatus();
  const { successNotification, errorNotification } = useNotification();
  const [selectedInspector, setSelectedInspector] = useState<ISelectedInspector[]>([]);
  const [selectedUniqueInspector, setSelectedUniqueInspector] = useState<
    ISelectedUniqueInspector[]
  >([]);
  const { taskData } = useSelectedTask(taskId);
  const [assignedInspectors] = useState<ISelectedUniqueInspector[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [comment, setComment] = useState<string>(inspectionLevelComment || '');
  const [addFieldInspectorDialog, setAddFieldInspectorDialog] = useState<boolean>(false);
  const [inspectorDeletedTaskId, setInspectorDeletedTaskId] = useState<number[]>([]);
  const { setInspectionList } = useInspectionList();
  const [intialFlag, setIntialFlag] = useState(false);
  const [inspectorType, setInspectorType] = useState<string>(FieldInspectorType.FI);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState<boolean>(false);
  const [fiListToAssignInspector, setFiListToAssignInspector] = useState<IViewInspectorDocument[]>(
    fieldInspectorList as IViewInspectorDocument[],
  );
  const [removeInspectorFlag, setRemoveInspectorFlag] = useState<boolean>(false);
  const offlineTextMsg = `User Should Be Connected To Internet To ${
    isUpdateInspector ? 'Update Inspector' : 'Assign Task'
  }`;
  const [isDECView, setIsDECView] = useState(false);
  const [disableToggleButton, setDisableToggleButton] = useState(false);
  const [isAssignedDECRemoved, setIsAssignedDECRemoved] = useState(false);
  const [titleText, setTitleText] = useState(title);
  const isDECUser = !!CurrentUserSelected?.currentUser?.clerkId;

  const handleToggleChange = (
    _event: React.MouseEvent<HTMLElement, MouseEvent>,
    inspType: string,
  ) => {
    if (inspType !== null) {
      setInspectorType(inspType);
    }
  };

  const selectedFilteredInspectors = useMemo(() => {
    return selectedInspector?.filter(item => {
      if (inspectorType === FieldInspectorType.DEC) {
        return item.clerkId;
      } else {
        return item.fieldInspectorId;
      }
    });
  }, [selectedInspector, inspectorType]);

  const showDECFlow = useMemo(() => {
    return !isDECUser && !isFromInspectedBy && (!taskList || (taskList && taskList.length < 2));
  }, [isDECUser, isFromInspectedBy, taskList]);

  useEffect(() => {
    if (!isDECUser && !isFromInspectedBy) {
      const isDECViewSelected = inspectorType === FieldInspectorType.DEC;
      setIsDECView(isDECViewSelected);
      setDisableToggleButton(isDECViewSelected && selectedFilteredInspectors?.length > 0);
      setTitleText(isDECViewSelected ? title?.replace('Inspector', 'Data Entry Clerk') : title);
    }
  }, [inspectorType, selectedInspector, title]);

  const uniqueFiListToAssignInspector = useMemo(() => {
    const [selectedClerk] = selectedUniqueInspector?.filter(item => item.clerkId);
    const filteredList = fiListToAssignInspector.filter(item => {
      if (isDECView) {
        return item.clerkId && item.clerkId !== selectedClerk?.clerkId;
      } else {
        return item.fieldInspectorId;
      }
    });
    const uniqueList = filteredList.reduce(
      (acc, item) => {
        const name = isDECView ? item.clerkName : item.fieldInspectorName;
        if (!acc.names.has(name)) {
          acc.names.add(name);
          acc.list.push(item);
        }
        return acc;
      },
      { names: new Set(), list: [] },
    ).list;
    return uniqueList;
  }, [fiListToAssignInspector, selectedUniqueInspector, isDECView]);

  useEffect(() => {
    if (taskList) {
      taskList
        .filter((e: TaskList) => e.fkAssetInspection === inspectionId)
        .forEach((e: TaskList) => {
          const index = assignedInspectors.findIndex(
            value => value.taskFiId === e.pkAssetInspectionTask,
          );
          if (index === -1) {
            const fiObj = {
              fieldInspectorId: e?.fkInspectorField,
              fieldInspectorName: e?.name,
              isActive: e?.isActive,
              taskUuid: e?.pkUuId,
              taskFiId: e?.pkAssetInspectionTask,
              inspectionId: e?.fkAssetInspection,
              taskStatus: e?.fkInspectionTaskStatusCategory,
              recommendations: e?.recommendations,
              dateCompleted: e?.dateCompleted,
              clerkId: e?.clerkId,
              clerkName: e?.clerkName,
            };
            assignedInspectors.push(fiObj);
          }
        });
    }
  }, [taskList]);

  useEffect(() => {
    if (assignedInspectors && selectedUniqueInspector.length !== assignedInspectors.length) {
      const clonedArray = JSON.parse(JSON.stringify(assignedInspectors));
      if (!isDECUser && clonedArray && clonedArray.length && clonedArray[0].clerkId) {
        setInspectorType(FieldInspectorType.DEC);
      }
      setSelectedInspector([...selectedInspector, ...clonedArray]);
      setSelectedUniqueInspector(
        [
          ...selectedUniqueInspector,
          ...clonedArray,
          ...(selectedProxyInspector ? [selectedProxyInspector] : []),
        ].filter(item => !(item?.clerkId && isDECUser)),
      );
    }
  }, [assignedInspectors]);

  const setInitial = async () => {
    setIntialFlag(true);
    const temp = selectedUniqueInspector.filter((value, index, self) => {
      const duplicateIndex = self.findIndex(t => t.fieldInspectorId === value.fieldInspectorId);
      if (index !== self.findIndex(t => t.fieldInspectorId === value.fieldInspectorId)) {
        selectedUniqueInspector[duplicateIndex].count = selectedUniqueInspector[duplicateIndex]
          .count
          ? (selectedUniqueInspector[duplicateIndex]?.count || 0) + 1
          : 2;
      }
      return index === self.findIndex(t => t.fieldInspectorId === value.fieldInspectorId);
    });
    setSelectedUniqueInspector(temp);
  };

  useEffect(() => {
    if (selectedUniqueInspector.length === assignedInspectors.length && !intialFlag) {
      setInitial();
    }
  }, [selectedUniqueInspector, assignedInspectors]);

  const setInspector = (inspectorId: number, name: string, clerkId: number, clerkName: string) => {
    if (clerkId || isAssignedDECRemoved) {
      setSelectedInspector([
        {
          fieldInspectorId: inspectorId,
          fieldInspectorName: name,
          clerkId,
          clerkName,
          isActive: true,
          taskUuid: selectedInspector[0]?.taskUuid,
          taskFiId: selectedInspector[0]?.taskFiId,
        },
      ]);
    } else {
      setSelectedInspector([
        ...selectedInspector,
        {
          fieldInspectorId: inspectorId,
          fieldInspectorName: name,
          clerkId,
          isActive: true,
          clerkName,
        },
      ]);
    }
  };

  const setUniqueInspector = (
    inspectorId: number,
    name: string,
    clerkId?: number,
    clerkName?: string,
    count?: number,
  ) => {
    if (clerkId || isAssignedDECRemoved || isFromInspectedBy) {
      setSelectedUniqueInspector([
        {
          fieldInspectorId: inspectorId,
          fieldInspectorName: name,
          isActive: true,
          count: count || 0,
          clerkId,
          taskUuid: selectedUniqueInspector[0]?.taskUuid,
          clerkName,
        },
      ]);
    } else {
      setSelectedUniqueInspector([
        ...selectedUniqueInspector,
        {
          fieldInspectorId: inspectorId,
          fieldInspectorName: name,
          isActive: true,
          count: count || 0,
          clerkId,
        },
      ]);
    }
  };

  const handleDeleteInspector = (inspId?: number, clerkId?: number) => {
    const result = selectedInspector
      .filter((inspector: ISelectedInspector) => {
        if (!isDECView) {
          return inspector.fieldInspectorId && inspector.fieldInspectorId === inspId;
        } else {
          return inspector.clerkId === clerkId;
        }
      })
      .map((val: ISelectedInspector) => {
        if (val?.taskFiId) {
          return val.taskFiId;
        }
      })
      .filter(x => x);

    if (result && result.length) {
      setRemoveInspectorFlag(true);
      setInspectorDeletedTaskId([...inspectorDeletedTaskId, ...(result as unknown as number[])]);
    }
    setSelectedUniqueInspector(
      selectedUniqueInspector.filter((inspector: ISelectedUniqueInspector) => {
        if (!isDECView) {
          if (inspector.fieldInspectorId) {
            return inspector.fieldInspectorId !== inspId;
          }
          return false;
        } else {
          return inspector.clerkId !== clerkId;
        }
      }),
    );
    if (
      !assignedInspectors?.find((e: ISelectedInspector) => {
        return e.fieldInspectorId === inspId;
      }) ||
      isDECView
    ) {
      setSelectedInspector(
        selectedInspector.filter((inspector: ISelectedInspector) => {
          if (!isDECView) {
            if (inspector.fieldInspectorId) {
              return inspector.fieldInspectorId !== inspId;
            }
            return false;
          } else {
            return inspector.clerkId !== clerkId;
          }
        }),
      );
      if (isDECView && selectedInspector[0].taskUuid) {
        setIsAssignedDECRemoved(true);
      }
    } else {
      selectedInspector.forEach((inspector: ISelectedInspector, index: number) => {
        if (
          (inspector.fieldInspectorId && inspector.fieldInspectorId === inspId) ||
          (inspector.clerkId && inspector.clerkId === clerkId)
        ) {
          if (inspector.taskUuid) {
            selectedInspector[index].isActive = false;
          } else {
            delete selectedInspector[index];
          }
        }
      });
    }
    const index = fiListToAssignInspector.findIndex((inspector: IViewInspectorDocument) => {
      if (!isDECView) {
        return inspector.fieldInspectorId && inspector.fieldInspectorId === inspId;
      } else {
        return inspector.clerkId === clerkId;
      }
    });

    if (index !== -1 && fiListToAssignInspector[index].isNew) {
      setFiListToAssignInspector(
        fiListToAssignInspector.filter((inspector: IViewInspectorDocument) => {
          if (!isDECView) {
            if (inspector.fieldInspectorId) {
              return inspector.fieldInspectorId !== fiListToAssignInspector[index].fieldInspectorId;
            }
          } else {
            if (inspector.clerkId) {
              return inspector.clerkId !== fiListToAssignInspector[index].clerkId;
            }
          }
          return false;
        }),
      );
    }
  };

  const onCommentsInput = (event: React.FocusEvent<HTMLInputElement>) => {
    setComment(event.target.value);
  };

  const saveBulkTaskOnInspection = async () => {
    const isAssignmentByFI = !isAssignToLead;
    const inspectionIds = HandleGesture.inspectionIds.toJSON();
    const listOfInspections: { inspectionId: number }[] = inspectionIds.map((value: number) => {
      return { inspectionId: value };
    });
    let selectedInspectorsWithInspId: InspectorData[] = [];

    inspectionIds.map((eachInspectionId: number) => {
      selectedInspector.map((inspector: ISelectedInspector) => {
        selectedInspectorsWithInspId = [
          ...selectedInspectorsWithInspId,
          { ...inspector, inspectionId: eachInspectionId },
        ];
      });
    });

    const mobileFieldInspectorInspections = {
      pkAssetInspectionTask: null,
      fkSchedule: CurrentUserSelected.workPackageId,
      fkInspectorLead: leadInspectorId,
      fkInspectorField: 0,
      taskDescription: '',
      isReviewedByLI: false,
      isReviewedByFI: false,
      commentInspectorLead: '',
      commentInspectorField: '',
      site: SiteDiscipline.siteId,
      discipline: SiteDiscipline.disciplineId,
      isActive: true,
      fkInspectionTaskStatusCategory: TaskStatus.ASSIGNED,
      pkUuId: uuidv4(),
      recommendations: '',
      dateCompleted: null,
      isPerformedByFI: isAssignmentByFI,
      isBulkReAssignment: true,
    };
    const role = isAssignmentByFI ? InspectorRole.FIELD : InspectorRole.LEAD;
    const varialblesList = [];
    selectedInspector.forEach(inspector => {
      varialblesList.push({
        mobileFieldInspectorInspections: {
          ...mobileFieldInspectorInspections,
          fkInspectorField: inspector.fieldInspectorId || 0,
          clerkId: inspector.clerkId || 0,
        },
        role,
        inspectionIdList: inspectionIds,
        taskIdList: [],
      });
    });

    try {
      let position = 0;
      let results: unknown[] = [];

      while (position < varialblesList.length) {
        const itemsForBatch = varialblesList.slice(position, position + BatchSize.ASSIGN);
        results = [
          ...results,
          ...(await Promise.all(
            itemsForBatch.map(async variables => {
              try {
                await bulkAssignOrReassign(variables);
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
                // console logs will be removed once sentry implementation is done
                sentryCaptureException(
                  error,
                  '~ file: AssignToMeDialog.tsx ~ line 120 ~ saveBulkTaskOnInspection ~',
                  'bulkOperation',
                  JSON.stringify({ ...variables, error: JSON.stringify(error) }),
                );
              }
            }),
          )),
        ];
        position += BatchSize.ASSIGN;
      }

      if (results.length === varialblesList.length) {
        selectedUniqueInspector.map(async (inspector: ISelectedUniqueInspector) => {
          await emailForTaskAssignment(
            CurrentUserSelected.workPackageId,
            inspector.fieldInspectorId || 0,
            leadInspectorId,
            inspector.clerkId || 0,
            listOfInspections,
            isAssignmentByFI,
          );
        });
        return true;
      }
    } catch (error) {
      return false;
    }
  };

  const saveBulkComment = async () => {
    const inspectionIds = HandleGesture.inspectionIds.toJSON();
    if (!comment.length) {
      return true;
    }
    try {
      let saved = true;
      for (const eachInspectionId in inspectionIds) {
        const result = await saveInspectionComment({
          inspectionId: inspectionIds[eachInspectionId],
          assignmentComment: comment,
        });
        if (result.errors) {
          saved = false;
        }
      }
      return saved;
    } catch (error) {
      return false;
    }
  };

  const handleBulkUpload = async () => {
    const isSuccess = await saveBulkTaskOnInspection();
    if (isSuccess) {
      const commentResult = await saveBulkComment();
      if (!commentResult) {
        errorNotification('Comment Not Saved');
      } else {
        db?.startReplication();
        successNotification(
          `${!isDECView ? 'Field Inspector' : 'Data Entry Clerk'} Updated Successfully`,
        );
        setIsLoading(false);
        setOpenAssignInspector(false);
        HandleGesture.resetEverything();
        HandleGesture.setIsCompleted(true);
        HandleGesture.setRunOnInspectionSubscription(true);
      }
    } else {
      errorNotification(
        `${!isDECView ? 'Field Inspector' : 'Data Entry Clerk'} is not saved please try again`,
      );
    }
  };

  const saveInspectors = async (
    inspectorList: {
      fieldInspectorId: number;
      clerkId: number;
    }[],
  ) => {
    let saved = true;
    for (const inspector in inspectorList.filter(item => {
      if (isDECView) {
        return item.clerkId;
      } else {
        return item.fieldInspectorId;
      }
    })) {
      const result = await saveFieldInspectorApi(
        CurrentUserSelected.workPackageId,
        leadInspectorId,
        inspectorList[inspector].fieldInspectorId,
        inspectorList[inspector].clerkId,
      );
      if (result?.errors) {
        saved = false;
      }
    }
    return saved;
  };

  const savecomment = async () => {
    if (!comment.length) return true;
    try {
      const result = await saveInspectionComment({
        inspectionId,
        assignmentComment: comment,
      });
      if (result.errors) {
        throw new Error(`Error`);
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  const saveTaskOnInspection = async () => {
    const pkAssetInspectionTask = 0;
    const isReviewedByLI = false;
    const isReviewedByFI = false;
    const commentInspectorLead = '';
    const commentInspectorField = '';
    let saved = true;
    const filteredSelectedInspectors = selectedInspector.filter(
      item => !(item.clerkId && isDECUser),
    );
    for (const inspector in filteredSelectedInspectors) {
      const result = await assignInspector(
        filteredSelectedInspectors[inspector].taskFiId
          ? filteredSelectedInspectors[inspector].taskFiId || 0
          : pkAssetInspectionTask,
        CurrentUserSelected.workPackageId,
        inspectionId,
        leadInspectorId,
        !isDECUser ? filteredSelectedInspectors[inspector].fieldInspectorId : 0,
        '',
        isReviewedByLI,
        isReviewedByFI,
        commentInspectorLead,
        commentInspectorField,
        filteredSelectedInspectors[inspector].isActive,
        filteredSelectedInspectors[inspector].clerkId
          ? filteredSelectedInspectors[inspector].fieldInspectorName
          : '',
        filteredSelectedInspectors[inspector].clerkId ||
          CurrentUserSelected?.currentUser?.clerkId ||
          0,
        SiteDiscipline.siteId,
        SiteDiscipline.disciplineId,
        filteredSelectedInspectors[inspector].taskUuid
          ? filteredSelectedInspectors[inspector].taskUuid || ''
          : uuidv4(),
        filteredSelectedInspectors[inspector]?.taskStatus,
        isDECUser,
        isNull(filteredSelectedInspectors[inspector], 'recommendations', ''),
        isNull(filteredSelectedInspectors[inspector], 'dateCompleted', null),
      );
      if (result?.errors) {
        saved = false;
      }
    }
    return saved;
  };
  const updateInspector = async () => {
    setIsLoading(true);
    const isReassignmentByFI = taskData?.fkInspectorLead !== loggedInUser?.inspectorId;
    try {
      const result = await assignInspector(
        taskData?.pkAssetInspectionTask,
        taskData?.fkSchedule,
        taskData?.fkAssetInspection,
        taskData?.fkInspectorLead,
        Number(selectedInspector[0]?.fieldInspectorId),
        comment,
        taskData?.isReviewedByLI,
        taskData?.isReviewedByFI,
        taskData?.commentInspectorLead,
        taskData?.commentInspectorField,
        true,
        taskData?.proxyInspector || '',
        Number(selectedInspector[0]?.clerkId || 0),
        taskData?.site,
        taskData?.discipline,
        taskData?.pkUuId,
        5,
        !!CurrentUserSelected?.currentUser?.clerkId,
        isNull(taskData, 'recommendations', ''),
        isNull(taskData, 'dateCompleted', null),
        isReassignmentByFI,
      );
      if (!result?.errors) {
        successNotification(
          `${!isDECView ? 'Field Inspector' : 'Data Entry Clerk'} re-assigned Successfully`,
        );
        setIsLoading(false);
        db?.startReplication();
        setOpenAssignInspector(false);
      }
    } catch (error) {
      errorNotification(
        `${!isDECView ? 'Field Inspector' : 'Data Entry Clerk'} is not updated please try again`,
      );
      setIsLoading(false);
    }
  };

  const onAssign = async () => {
    setIsLoading(true);
    let savedInspectorOnWorkPackageLevel = false;
    if (isFromInspectedBy) {
      const filteredSelectedInspectors = selectedInspector.filter(item => !item.clerkId);
      setIsLoading(false);
      setOpenAssignInspector(false);
      handleProxyInspectorChange({
        fieldInspectorName: filteredSelectedInspectors[0].fieldInspectorName,
      });
      return;
    }

    const newInspectors = fiListToAssignInspector.filter(
      (inspector: IViewInspectorDocument) => inspector.isNew,
    );
    if (newInspectors?.length) {
      savedInspectorOnWorkPackageLevel = await saveInspectors(
        fiListToAssignInspector.filter((inspector: IViewInspectorDocument) => inspector.isNew),
      );
    }

    if (savedInspectorOnWorkPackageLevel || !newInspectors.length) {
      if (isJestTest() ? isJestTestCase : HandleGesture.bulkUpload) {
        await handleBulkUpload();
      } else {
        if (isUpdateInspector) {
          updateInspector();
        } else {
          const savedInspectorOnIspectionLevel = await saveTaskOnInspection();
          if (savedInspectorOnIspectionLevel) {
            await setInspectionList(inspectionId.toString(), 5);
          }
        }
        if (inspectionLevelComment !== comment) {
          const commentResult = await savecomment();
          if (!commentResult) {
            errorNotification('Comment Not Saved');
          }
        }
        setIsLoading(false);
        db?.startReplication();
        setOpenAssignInspector(false);
        if (!isUpdateInspector) {
          successNotification(
            `${!isDECView ? 'Field Inspector' : 'Data Entry Clerk'} Updated Successfully`,
          );
        }
      }
    } else {
      errorNotification('Error while adding inspector please try again');
    }
    setIsLoading(false);
  };

  const getDivider = () => {
    return <Divider orientation="horizontal" className={classes.divider} />;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === Key.Enter && selectedInspector.length > 0) {
      onAssign();
    }
  };

  const handleSearchForAssign = (
    event: React.KeyboardEvent<HTMLDivElement>,
    value: {
      fieldInspectorId: number;
      fieldInspectorName: string;
      clerkName: string;
      clerkId: number;
    },
  ) => {
    if (event.key === Key.Enter && selectedInspector.length > 0) {
      onAssign();
      return;
    }
    if (value?.fieldInspectorId === loggedInUser?.inspectorId) {
      //TODO
      setInspector(loggedInUser.inspectorId, loggedInUser.name, 0);
    } else {
      setInspector(
        value?.fieldInspectorId,
        value?.fieldInspectorName,
        value?.clerkId,
        value?.clerkName,
      );
    }
    const index = selectedUniqueInspector.findIndex(
      (inspector: { fieldInspectorId: number; clerkId: number }) =>
        inspector.clerkId
          ? inspector.clerkId === value.clerkId
          : inspector.fieldInspectorId === value.fieldInspectorId,
    );
    if (index === -1 || isFromInspectedBy) {
      if (value?.fieldInspectorId === loggedInUser?.inspectorId) {
        //TODO
        setUniqueInspector(loggedInUser.inspectorId, loggedInUser.name);
      } else {
        setUniqueInspector(
          value?.fieldInspectorId,
          value?.fieldInspectorName,
          value?.clerkId,
          value?.clerkName,
        );
      }
    } else {
      selectedUniqueInspector[index].count = selectedUniqueInspector[index].count
        ? (selectedUniqueInspector[index]?.count || 0) + 1
        : 2;
    }
  };

  const handleSearch = (
    event: React.KeyboardEvent<HTMLDivElement>,
    value: {
      pkInspector: number;
      firstName: string;
      lastName: string;
      clerkId: number;
      clerkName: string;
    },
  ) => {
    handleSearchForAssign(event, {
      fieldInspectorId: value.pkInspector,
      fieldInspectorName: `${`${value.firstName} ${value.lastName}`}`,
      clerkId: value.clerkId,
      clerkName: value.clerkName,
    });
    const index = fiListToAssignInspector.findIndex(
      (inspector: { fieldInspectorId?: number; clerkId?: number }) => {
        if (inspector?.fieldInspectorId) {
          return inspector.fieldInspectorId === value.pkInspector;
        } else {
          return inspector.clerkId === value.clerkId;
        }
      },
    );
    if (index === -1) {
      setFiListToAssignInspector([
        ...fiListToAssignInspector,
        {
          fieldInspectorName: `${`${value.firstName} ${value.lastName}`}`,
          fieldInspectorId: value.pkInspector,
          clerkId: value.clerkId,
          isNew: true,
          clerkName: value.clerkName,
        },
      ]);
    }
  };
  const openDialog = () => {
    setOpenConfirmationDialog(true);
  };

  const handleOnSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selectedInspector.length > 0) {
      onAssign();
    }
  };

  return (
    <>
      <Dialog
        open={openAssignInspector}
        titleText={titleText}
        onClose={() => {
          if (!isLoading) {
            setOpenAssignInspector(false);
            setSelectedInspector([]);
          }
        }}
        backButton={false}
        dialogActions={
          online ? (
            <CustomButton
              isLoading={isLoading}
              onClose={() => {
                if (!isLoading) {
                  if (
                    title === 'Update Inspector' &&
                    removeInspectorFlag &&
                    !isAssignedDECRemoved
                  ) {
                    openDialog();
                  } else {
                    onAssign();
                  }
                }
              }}
              disabled={!selectedFilteredInspectors?.length}
              buttonText={isUpdateInspector ? 'Save' : isDECUser ? 'Add Inspector' : 'Assign'}
            />
          ) : (
            <></>
          )
        }
        maxWidth="xs"
        noMinHeight
        disableEnforceFocus={isLoading ? true : undefined}
        BackdropProps={{
          onClick: event => {
            if (isLoading) event.preventDefault();
          },
          onTouchStart: event => {
            if (isLoading) event.preventDefault();
          },
          style: { touchAction: isLoading ? 'none' : 'auto' },
        }}
      >
        <>
          <form onSubmit={handleOnSubmit}>
            {online ? (
              <>
                <Grid item container data-testid="assign-inspector-grid">
                  {showDECFlow ? (
                    <>
                      <ToggleButtonGroup
                        color="#121212"
                        value={inspectorType}
                        exclusive
                        onChange={handleToggleChange}
                      >
                        <ToggleButton
                          disabled={disableToggleButton}
                          value={FieldInspectorType.FI}
                          data-testid="nutrientogglebutton"
                        >
                          Field Inspector
                        </ToggleButton>
                        <ToggleButton
                          disabled={disableToggleButton}
                          value={FieldInspectorType.DEC}
                          data-testid="sptogglebutton"
                        >
                          Data Entry Clerk
                        </ToggleButton>
                      </ToggleButtonGroup>
                      {isDECView && disableToggleButton && (
                        <>
                          <Typography
                            variant="body2"
                            style={{ fontSize: '12px', color: '#93C7EB' }}
                            data-testid="dec-info"
                          >
                            <Icons.InfoFeather
                              className={classes.infoFeather}
                              size="small"
                              strokeWidth="2"
                            />
                            <p style={{ float: 'right' }}>
                              Remove the selected Data Entry Clerk(s) to enable in the toggle
                            </p>
                          </Typography>
                        </>
                      )}
                      {getDivider()}
                    </>
                  ) : taskList && (isDECUser || !isFromInspectedBy) ? (
                    <>
                      <Grid item container alignItems="center">
                        <Typography
                          variant="subtitle2"
                          className="assignInspectorLabel"
                          data-testid="title-id"
                        >
                          Lead Inspector
                        </Typography>
                      </Grid>
                      <Grid item container alignItems="center" style={{ marginTop: '10px' }}>
                        <Typography
                          variant="body2"
                          className="assignInspectorLabel"
                          data-testid="title-id"
                        >
                          {taskList[0]?.leadInspectorName || ''}
                        </Typography>
                      </Grid>
                      {getDivider()}
                    </>
                  ) : null}
                  <>
                    <Typography
                      variant="subtitle2"
                      className="assignInspectorLabel"
                      data-testid="title-id"
                    >
                      {isFromInspectedBy
                        ? 'Inspected By'
                        : `Assign ${!isDECView ? 'Inspector' : 'Data Entry Clerk'}`}
                    </Typography>
                    <AutoComplete
                      autoSelect={false}
                      key="search-inspector"
                      disabled={isLoading}
                      noMinHeight
                      size="small"
                      closeIcon=""
                      startAdornment={
                        <InputAdornment position="start">
                          <Icons.SearchFeather
                            color="rgba(149, 149, 149, 1)"
                            className={classes.searchFeaIcon}
                          />
                        </InputAdornment>
                      }
                      endAdornment={
                        <InputAdornment position="end">
                          <Icons.PlusCircleFeather
                            className={classes.plusIcon}
                            color="primary"
                            strokeWidth="1.5px"
                          />
                        </InputAdornment>
                      }
                      placeholder={`Search ${!isDECView ? 'Inspector' : 'Data Entry Clerk'}`}
                      className={classes.autoComplete}
                      onChange={(event, newValue) => {
                        if (newValue && !isLoading) handleSearchForAssign(event, newValue);
                      }}
                      list={uniqueFiListToAssignInspector?.sort(sortByInspectorName)}
                      value={{ fieldInspectorName: '' }}
                      getOptionLabel={option => option.fieldInspectorName || option.clerkName || ''}
                      data-testid="assignInspector-id"
                    />
                    {(addFieldInspectorDialog ||
                      isFromInspectedBy ||
                      selectedUniqueInspector?.filter(
                        (e: ISelectedUniqueInspector) => e.isActive !== false,
                      )?.length > 0) &&
                      getDivider()}
                  </>
                </Grid>
                <Grid container className={classes.root}>
                  {(selectedUniqueInspector.filter(item => {
                    if (isDECView && !isDECUser) {
                      return item.clerkId;
                    } else {
                      return item.fieldInspectorId;
                    }
                  }).length > 0 ||
                    selectedProxyInspector?.fieldInspectorName) && (
                    <>
                      <Grid item container xs={12} alignItems="center">
                        <Grid item container alignItems="center">
                          <Typography variant="body2" className={classes.nameLabel}>
                            Selected {!isDECView ? 'Inspector' : 'Data Entry Clerk'}
                          </Typography>
                        </Grid>
                        <Grid item container>
                          {selectedUniqueInspector
                            ?.filter((e: ISelectedUniqueInspector) => e.isActive !== false)
                            ?.map((inspector: ISelectedUniqueInspector) => {
                              return (
                                <StyledButton
                                  inspector={inspector}
                                  handleDeleteInspector={() =>
                                    handleDeleteInspector(
                                      inspector.fieldInspectorId,
                                      inspector.clerkId,
                                    )
                                  }
                                  count={inspector?.count ? inspector?.count : 0}
                                  canDelete={!isLoading}
                                />
                              );
                            })}
                        </Grid>
                      </Grid>
                      {getDivider()}
                    </>
                  )}
                  {(selectedUniqueInspector?.filter(
                    (e: ISelectedUniqueInspector) => e.isActive !== false,
                  )?.length > 0 ||
                    addFieldInspectorDialog) &&
                    !isDECUser &&
                    !isFromInspectedBy && (
                      <>
                        <Grid item container className={classes.commentsGrid}>
                          <Typography variant="body2" className="assignInspectorLabel">
                            Comments
                          </Typography>
                          <TextField
                            defaultValue={comment}
                            onKeyDown={handleKeyDown}
                            onChange={onCommentsInput}
                            noMinHeight
                            fullWidth
                            variant="outlined"
                            size="small"
                            multiline
                            placeholder="Type"
                            rows={2}
                            maxRows={2}
                            className={classes.commentBox}
                            disabled={isLoading}
                            inputProps={{ 'data-testid': 'commentbox' }}
                          />
                        </Grid>
                        {addFieldInspectorDialog && getDivider()}
                      </>
                    )}
                  {getValueFromLocalStorage('isLeadInspector') === TRUE &&
                    !addFieldInspectorDialog &&
                    !isLoading &&
                    !isFromInspectedBy && (
                      <>
                        {getDivider()}
                        <Grid
                          item
                          container
                          xs={12}
                          alignItems="center"
                          className={classes.fieldInspectorGrid}
                        >
                          <Button
                            color="primary"
                            className="addFieldInspectorButton"
                            noMinHeight
                            onClick={() => setAddFieldInspectorDialog(true)}
                            data-testid="addFieldInspector"
                          >
                            <Icons.PlusCircleFeather
                              color="primary"
                              className={classes.addFieldInspectorIcon}
                              strokeWidth="1.5px"
                            />
                            <Typography
                              variant="subtitle2"
                              className={classes.fieldInspectorButton}
                            >
                              Add {!isDECView ? 'Field Inspector' : 'Data Entry Clerk'}
                            </Typography>
                          </Button>
                        </Grid>
                      </>
                    )}
                </Grid>
              </>
            ) : (
              <Typography variant="subtitle1" fontWeight={300} className={classes.offlineMessage}>
                {offlineTextMsg}
              </Typography>
            )}
          </form>
          {(addFieldInspectorDialog || isFromInspectedBy) && (
            <AddInspector
              handleSearch={handleSearch}
              fiListToAssignInspector={fiListToAssignInspector.filter(item => {
                if (isDECView) {
                  return item.clerkId;
                } else {
                  return item.fieldInspectorId;
                }
              })}
              isDECView={isDECView}
            />
          )}
        </>
      </Dialog>

      {openConfirmationDialog && (
        <DeleteTask
          open={openConfirmationDialog}
          onClose={() => setOpenConfirmationDialog(false)}
          taskData={inspectorDeletedTaskId}
          isInspectorDeletion
          isTaskDeletion
          onDeleteInspector={onAssign}
        />
      )}
    </>
  );
};

export default observer(AssignInspector);
